import Api from '../API';
import RecruitApiClient from '../../../RecruitApiClient';

jest.mock('../../../RecruitApiClient');
const mockRecruitApiClient = RecruitApiClient as jest.Mocked<typeof RecruitApiClient>;

describe('Candidate API', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  describe('getCandidateData', () => {
    it('should call RecruitApiClient.get with correct candidate ID', () => {
      const candidateId = 123;
      const mockResponse = {
        res: {
          id: candidateId,
          firstname: 'John',
          lastname: 'Doe'
        },
        resHead: {}
      };

      mockRecruitApiClient.get.mockResolvedValue(mockResponse);

      Api.getCandidateData(candidateId);

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}`,
        null
      );
    });
  });

  describe('updateCandidateTags', () => {
    it('should call RecruitApiClient.post with correct parameters', () => {
      const candidateId = 123;
      const tags = ['tag1', 'tag2', 'tag3'];
      const expectedPayload = { tags };

      mockRecruitApiClient.post.mockResolvedValue({ res: { success: true }, resHead: {} });

      Api.updateCandidateTags(candidateId, tags);

      expect(mockRecruitApiClient.post).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/update_tags`,
        expectedPayload
      );
    });
  });

  describe('getCandidateApplications', () => {
    it('should call RecruitApiClient.get with correct parameters', () => {
      const candidateId = 123;
      const params = {
        page: 1,
        limit: 10,
        sort: 'created_at',
        sort_order: 'desc'
      };

      mockRecruitApiClient.get.mockResolvedValue({ res: { applications: [] }, resHead: {} });

      Api.getCandidateApplications(candidateId, params);

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/applications`,
        params
      );
    });
  });

  describe('getCandidateTasks', () => {
    it('should call RecruitApiClient.get with correct candidate ID', () => {
      const candidateId = 123;

      mockRecruitApiClient.get.mockResolvedValue({ res: { tasks: [] }, resHead: {} });

      Api.getCandidateTasks(candidateId);

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/tasks`
      );
    });
  });

  describe('getAssignableUsers', () => {
    it('should call RecruitApiClient.get with correct endpoint', () => {
      mockRecruitApiClient.get.mockResolvedValue({ res: { users: [] }, resHead: {} });

      Api.getAssignableUsers();

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith('/api/v4/tasks/assignable_users');
    });
  });

  describe('createCandidateTask', () => {
    it('should call RecruitApiClient.post with correct parameters', () => {
      const candidateId = 123;
      const params = {
        subject: 'Test Task',
        note: 'Test note',
        priority: 1,
        due_at: '2023-12-31',
        user_id: 456
      };

      mockRecruitApiClient.post.mockResolvedValue({ res: { id: 1 }, resHead: {} });

      Api.createCandidateTask(candidateId, params);

      expect(mockRecruitApiClient.post).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/tasks`,
        params
      );
    });
  });

  describe('deleteCandidateTask', () => {
    it('should call RecruitApiClient.delete with correct candidate and task IDs', () => {
      const candidateId = 123;
      const taskId = 456;

      mockRecruitApiClient.delete.mockResolvedValue({ res: { success: true }, resHead: {} });

      Api.deleteCandidateTask(candidateId, taskId);

      expect(mockRecruitApiClient.delete).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/tasks/${taskId}`
      );
    });
  });

  describe('updateCandidateTask', () => {
    it('should call RecruitApiClient.put with correct parameters', () => {
      const candidateId = 123;
      const taskId = 456;
      const params = {
        subject: 'Updated Task',
        note: 'Updated note',
        priority: 2,
        completed_at: '2023-12-31'
      };

      mockRecruitApiClient.put.mockResolvedValue({ res: { id: taskId }, resHead: {} });

      Api.updateCandidateTask(candidateId, taskId, params);

      expect(mockRecruitApiClient.put).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/tasks/${taskId}`,
        params
      );
    });
  });

  describe('getWorkflows', () => {
    it('should call RecruitApiClient.get with correct candidate ID', () => {
      const candidateId = 123;

      mockRecruitApiClient.get.mockResolvedValue({ res: { workflows: [] }, resHead: {} });

      Api.getWorkflows(candidateId);

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith(
        `/api/v4/workflows?candidate_id=${candidateId}`
      );
    });
  });

  describe('assignWorkflow', () => {
    it('should call RecruitApiClient.post with correct parameters', () => {
      const candidateId = 123;
      const params = {
        id: 1,
        user_id: 456
      };

      mockRecruitApiClient.post.mockResolvedValue({ res: { success: true }, resHead: {} });

      Api.assignWorkflow(candidateId, params);

      expect(mockRecruitApiClient.post).toHaveBeenCalledWith(
        `/api/v4/workflows?candidate_id=${candidateId}`,
        params
      );
    });
  });

  describe('getCandidateActivities', () => {
    it('should call RecruitApiClient.get with correct candidate ID', () => {
      const candidateId = 123;

      mockRecruitApiClient.get.mockResolvedValue({ res: { activities: [] }, resHead: {} });

      Api.getCandidateActivities(candidateId);

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/activities`
      );
    });
  });

  describe('addCandidateNote', () => {
    it('should call RecruitApiClient.post with correct parameters', () => {
      const candidateId = 123;
      const params = {
        subject: 'Test Note',
        body: 'Test note body'
      };

      const expectedPayload = {
        note: params
      };

      mockRecruitApiClient.post.mockResolvedValue({ res: { success: true }, resHead: {} });

      Api.addCandidateNote(candidateId, params);

      expect(mockRecruitApiClient.post).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/add_permanent_record`,
        expectedPayload
      );
    });
  });

  describe('mergeCandidates', () => {
    it('should call RecruitApiClient.post with correct candidate ID', () => {
      const candidateId = 123;

      mockRecruitApiClient.post.mockResolvedValue({ res: { success: true }, resHead: {} });

      Api.mergeCandidates(candidateId);

      expect(mockRecruitApiClient.post).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/merge`
      );
    });
  });

  describe('updateCandidateNotepad', () => {
    it('should call RecruitApiClient.put with correct parameters', () => {
      const candidateId = 123;
      const body = 'Test notepad content';

      mockRecruitApiClient.put.mockResolvedValue({ res: { success: 'Updated' }, resHead: {} });

      Api.updateCandidateNotepad(candidateId, body);

      expect(mockRecruitApiClient.put).toHaveBeenCalledWith(
        `/api/v4/candidates/${candidateId}/update_notepad`,
        { body }
      );
    });
  });

  describe('API error handling', () => {
    it('should propagate errors from RecruitApiClient', async () => {
      const error = new Error('Network error');
      mockRecruitApiClient.get.mockRejectedValue(error);

      await expect(Api.getCandidateData(123)).rejects.toThrow('Network error');
    });

    it('should handle different error types', async () => {
      const error = new Error('Validation failed');
      mockRecruitApiClient.post.mockRejectedValue(error);

      await expect(Api.updateCandidateTags(123, [])).rejects.toThrow('Validation failed');
    });
  });

  describe('API response handling', () => {
    it('should return response data correctly', async () => {
      const mockResponse = {
        res: {
          id: 123,
          firstname: 'John',
          lastname: 'Doe',
          email: 'john@example.com'
        },
        resHead: {}
      };

      mockRecruitApiClient.get.mockResolvedValue(mockResponse);

      const result = await Api.getCandidateData(123);

      expect(result).toEqual(mockResponse);
    });
  });
});
